import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert as ChakraAlert,
  Box,
  Code,
  Flex,
  Text,
} from '@chakra-ui/react';
import { AlertIcon } from '@ui/components/Alert';
import { diffChars } from 'diff';

type MasterSheetMismatchProps = {
  mismatch?: {
    formulasAdded: string[];
    formulasChanged: Array<{
      key: string;
      oldValue: string;
      newValue: string;
    }>;
    oldFormulaCount?: number;
    newFormulaCount?: number;
  };
};

export function MasterSheetMismatch({ mismatch }: MasterSheetMismatchProps) {
  if (!mismatch) {
    return null;
  }

  const { formulasAdded, formulasChanged, oldFormulaCount, newFormulaCount } =
    mismatch;

  const hasAddedFormulas = formulasAdded.length > 0;
  const hasChangedFormulas = formulasChanged.length > 0;

  return (
    <ChakraAlert status="warning" flexShrink={0} alignItems="flex-start">
      <AlertIcon status="warning" mt={1} />
      <Box flex={1} minW={0}>
        <Text fontWeight="semibold">
          Changes detected in the master sheet formulas
        </Text>

        {oldFormulaCount != null && newFormulaCount != null && (
          <Box mt={2}>
            <Text fontSize="sm" color="snap.gray.700">
              Formula count changed from {oldFormulaCount} to {newFormulaCount}
            </Text>
          </Box>
        )}

        {hasAddedFormulas && (
          <Box mt={2}>
            <Text fontSize="sm" color="snap.gray.700" mb={1}>
              {formulasAdded.length} new formula(s) added:
            </Text>
            <Flex gap={1} flexWrap="wrap">
              {formulasAdded.map((key) => (
                <Code key={key} fontSize="xs" colorScheme="green">
                  {key}
                </Code>
              ))}
            </Flex>
          </Box>
        )}

        {hasChangedFormulas && (
          <Box mt={2}>
            <Text fontSize="sm" color="snap.gray.700" mb={1}>
              {formulasChanged.length} formula(s) modified:
            </Text>
            <Accordion allowMultiple reduceMotion size="sm">
              {formulasChanged.map((change) => (
                <AccordionItem
                  key={change.key}
                  border="none"
                  mb={2}
                  backgroundColor="snap.gray.100"
                >
                  <AccordionButton
                    px={2}
                    py={1}
                    borderRadius="sm"
                    _hover={{ bg: 'snap.gray.100' }}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Text
                      fontSize="xs"
                      flex={1}
                      textAlign="left"
                      fontWeight="semibold"
                    >
                      {change.key}
                    </Text>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel p={0}>
                    <Box borderRadius="sm" p={2} overflowX="auto">
                      <FormulaDiff
                        oldValue={change.oldValue}
                        newValue={change.newValue}
                      />
                    </Box>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          </Box>
        )}

        <Text fontSize="xs" color="snap.gray.500" mt={3}>
          The master sheet formulas have diverged from the cached version. The
          import completed successfully, but the calculations differ from the
          code implementation.
        </Text>
      </Box>
    </ChakraAlert>
  );
}

type FormulaDiffProps = {
  oldValue: string;
  newValue: string;
};

function FormulaDiff({ oldValue, newValue }: FormulaDiffProps) {
  const changes = diffChars(oldValue, newValue);

  return (
    <Box
      fontFamily="mono"
      fontSize="xs"
      whiteSpace="pre-wrap"
      wordBreak="break-all"
    >
      {changes.map((part, index) => {
        if (part.added) {
          return (
            <Box
              as="span"
              key={index}
              bg="green.200"
              color="green.900"
              px="1px"
              borderRadius="sm"
            >
              {part.value}
            </Box>
          );
        }

        if (part.removed) {
          return (
            <Box
              as="span"
              key={index}
              bg="red.200"
              color="red.900"
              textDecoration="line-through"
              px="1px"
              borderRadius="sm"
            >
              {part.value}
            </Box>
          );
        }

        return (
          <Box as="span" key={index}>
            {part.value}
          </Box>
        );
      })}
    </Box>
  );
}
